import { COLOR_TOKENS, type KontrolDurumu } from '@mod/domain';

/**
 * SAHA HARİTASI — obje kataloğu ve yerleşim.
 *
 * ⚠️ KROKİ STATİK BİR GÖRSELDİR, interaktif harita servisi DEĞİL. Koordinat
 *    sistemi kroki görseli üzerinde RELATİF (0–100) tutulur; görsel
 *    değiştiğinde ya da farklı çözünürlükte gösterildiğinde pinler yerinde
 *    kalır. Enlem/boylam kullanmıyoruz — tesis krokisi coğrafi bir harita
 *    değil, orantılı bir çizim.
 */

/** Haritada gösterilen obje tipleri — LEJANT bu listeden üretilir. */
export type ObjeTipi = 'iskele_gecti' | 'iskele_yaklasiyor' | 'iskele_guncel' | 'malzeme';

export interface ObjeTanimi {
  etiket: string;
  aciklama: string;
  renk: string;
  /** SVG'de çizilen biçim — malzeme iskeleden GÖRSEL OLARAK ayrışmalı. */
  bicim: 'daire' | 'kare';
}

/**
 * ⚠️ MALZEME AYRI BİÇİM **VE** AYRI RENK. Kullanıcı isteği: *"kurulu
 *    iskeleden görsel olarak net ayrışmalı"*. Yalnız renk ayrımı yetmezdi —
 *    renk körlüğü bir yana, harita zaten üç renkli iskele taşıyor ve
 *    dördüncü bir renk "bu da bir iskele durumu" gibi okunurdu. Biçim farkı
 *    (kare) tipi anlatır, renk durumu.
 *
 * `stok` token'ı tasarım sisteminde zaten *"malzeme / depo / söküm-revize"*
 * anlamına atanmış (COLOR_TOKENS) — yeni renk uydurmuyoruz.
 */
export const OBJE_TANIMLARI: Record<ObjeTipi, ObjeTanimi> = {
  iskele_gecti: {
    etiket: 'Kontrol vadesi geçmiş',
    aciklama: 'Haftalık kontrolü gecikmiş kurulu iskele',
    renk: COLOR_TOKENS.crit,
    bicim: 'daire',
  },
  iskele_yaklasiyor: {
    etiket: 'Kontrol vadesi yaklaşan',
    aciklama: 'Vadesi yarın dolan kurulu iskele',
    renk: COLOR_TOKENS.warn,
    bicim: 'daire',
  },
  iskele_guncel: {
    etiket: 'Kontrolü güncel',
    aciklama: 'Vadesi gelmemiş kurulu iskele',
    renk: COLOR_TOKENS.ok,
    bicim: 'daire',
  },
  malzeme: {
    etiket: 'Sahada bırakılmış malzeme',
    aciklama: 'Sökümü bitti, malzemesi bölgede bırakıldı — toplanmayı bekliyor',
    renk: COLOR_TOKENS.stok,
    bicim: 'kare',
  },
};

/** Lejant sırası — en kritik üstte. */
export const LEJANT_SIRASI: ObjeTipi[] = [
  'iskele_gecti',
  'iskele_yaklasiyor',
  'iskele_guncel',
  'malzeme',
];

export function iskeleTipi(durum: KontrolDurumu): ObjeTipi {
  if (durum === 'gecti') return 'iskele_gecti';
  if (durum === 'yaklasiyor') return 'iskele_yaklasiyor';
  return 'iskele_guncel';
}

/* ================================================================== */
/*  GEÇİCİ YERLEŞİM — kroki ve gerçek pinler gelene kadar              */
/* ================================================================== */

/**
 * FNV-1a — kısa, bağımlılıksız, KARARLI karma.
 *
 * ⚠️ `Math.random()` KULLANILMIYOR ve bu kritik: rastgele konum her render'da
 *    yeniden üretilseydi pinler ekranda zıplar, kullanıcı aynı iskeleyi iki
 *    kez farklı yerde görürdü. Aynı id her zaman aynı noktayı verir.
 */
function karma(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) / 0xffffffff;
}

export interface Konum {
  x: number;
  y: number;
}

/**
 * ÜNİTE BAZINDA KÜMELENMİŞ, KARARLI GEÇİCİ YERLEŞİM.
 *
 * ⚠️ BU BİR YER TUTUCUDUR. Gerçek konum, keşfe giden kişinin kroki üzerinde
 *    bıraktığı pinden gelecek (`konum` alanı bugün null). O gelene kadar
 *    haritanın boş görünmemesi ve yapının denenebilmesi için üretiliyor.
 *
 * Neden ÜNİTEYE göre kümeleniyor: aynı ünitedeki iskeleler sahada da yan
 * yanadır. Tamamen rastgele dağıtsaydık harita gerçeğe hiç benzemez ve
 * yerleşimin işe yarayıp yaramadığı denenemezdi. Küme merkezi üniteden,
 * küme içindeki dağılım objenin kendi id'sinden türüyor.
 */
export function geciciKonum(objeId: string, unite: string): Konum {
  // Küme merkezi — kenarlardan uzak dursun (12–88 aralığı).
  const mx = 12 + karma(`u:${unite}`) * 76;
  const my = 14 + karma(`v:${unite}`) * 70;
  // Küme içi dağılım — kutupsal, böylece küme yuvarlak görünür.
  const aci = karma(`a:${objeId}`) * Math.PI * 2;
  const yaricap = Math.sqrt(karma(`r:${objeId}`)) * 7.5;
  return {
    x: Math.min(97, Math.max(3, mx + Math.cos(aci) * yaricap)),
    y: Math.min(96, Math.max(4, my + Math.sin(aci) * yaricap * 0.8)),
  };
}
